import React, { useCallback, useMemo, useState } from 'react';
import { FormControl, FormControlLabel, FormLabel, MenuItem, Radio, RadioGroup, TextField } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import { useMeetingStaffContext } from '../../../MeetingStaffContext';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import MotionVotingControls from '../../../MotionVotingControls';
import { ActivityType, RelatedType } from 'store/enums/BallotReviewTypes';
import { MeetingSession, Motion } from 'store/types/Meeting';
import { defaultTinyMCEProps } from 'util/form';
import {
  createImagesUploadHandler,
  excludeAttendee,
  getAttendeeOptions,
  meetingSessionToActivity,
  relatedTypeLabel,
  useSafeAttendeeDisplayValue,
} from 'util/meeting';
import { VotingRuleType } from 'store/enums/VoteTypes';

const RelatedMotion: React.FunctionComponent = () => {
  const { meetingId, attendees, meetingSession } = useMeetingHubContext();
  const { logActivity } = useMeetingStaffContext();
  const [type, setType] = useState<RelatedType | undefined>();
  const [reason, setReason] = useState('');
  const [by, setBy] = useState('');
  const [second, setSecond] = useState('');
  const [discussion, setDiscussion] = useState('');

  const byAttendees = useMemo(() => excludeAttendee(attendees || [], second), [attendees, second]);
  const secondAttendees = useMemo(() => excludeAttendee(attendees || [], by), [attendees, by]);

  const byAttendeeOptions = useMemo(
    () => getAttendeeOptions(byAttendees || [], meetingSession?.presidingChairId),
    [byAttendees, meetingSession?.presidingChairId]
  );
  const secondAttendeeOptions = useMemo(
    () => getAttendeeOptions(secondAttendees || [], meetingSession?.presidingChairId),
    [secondAttendees, meetingSession?.presidingChairId]
  );

  const safeBy = useSafeAttendeeDisplayValue(by, byAttendees);
  const safeSecond = useSafeAttendeeDisplayValue(second, secondAttendees);

  const currentMotion: Motion = useMemo(() => {
    return {
      id: `related-motion`,
      title: 'Related',
      statement: type ? relatedTypeLabel[type] : '',
      reason,
      by: safeBy,
      second: safeSecond,
      discussion,
    };
  }, [type, reason, safeBy, safeSecond, discussion]);

  const handleClearMotion = useCallback(() => {
    setType(undefined);
    setReason('');
    setBy('');
    setSecond('');
    setDiscussion('');
  }, []);

  const handleDisplayMotion = useCallback(
    (updatedSession: MeetingSession) => {
      logActivity?.({
        ...meetingSessionToActivity(updatedSession),
        type: ActivityType.Motion,
        motion: updatedSession.currentMotion,
      });
    },
    [logActivity]
  );

  return (
    <GridContainer>
      <GridItem>
        <FormControl>
          <FormLabel>Motion and Reason</FormLabel>
          <RadioGroup
            name="related-type-radio-group"
            value={type ?? ''}
            onChange={(event) => setType(event.target.value as RelatedType)}
          >
            <FormControlLabel
              value={RelatedType.Related}
              control={<Radio />}
              label={relatedTypeLabel[RelatedType.Related]}
            />
            <FormControlLabel
              value={RelatedType.NotRelated}
              control={<Radio />}
              label={relatedTypeLabel[RelatedType.NotRelated]}
            />
          </RadioGroup>
        </FormControl>
      </GridItem>
      {type === RelatedType.NotRelated && (
        <>
          <GridItem>
            <FormControl className="w-100">
              <FormLabel>Reason *</FormLabel>
              <Editor
                {...defaultTinyMCEProps}
                id="related-reason-editor"
                value={reason}
                onEditorChange={(newValue: string) => setReason(newValue)}
                init={{
                  ...defaultTinyMCEProps.init,
                  images_upload_handler: createImagesUploadHandler(meetingId),
                }}
              />
            </FormControl>
          </GridItem>
          <GridItem md={6}>
            <TextField
              label={'By'}
              select
              required
              value={safeBy}
              onChange={(event) => {
                setBy(event.target.value);
              }}
            >
              <MenuItem value={''}>-- None --</MenuItem>
              {byAttendeeOptions}
            </TextField>
          </GridItem>
          <GridItem md={6}>
            <TextField
              label={'Second'}
              select
              required
              value={safeSecond}
              onChange={(event) => {
                setSecond(event.target.value);
              }}
            >
              <MenuItem value={''}>-- None --</MenuItem>
              {secondAttendeeOptions}
            </TextField>
          </GridItem>
          <GridItem>
            <FormControl className="w-100">
              <FormLabel>Discussion</FormLabel>
              <Editor
                {...defaultTinyMCEProps}
                id="related-discussion-editor"
                value={discussion}
                onEditorChange={(newValue: string) => setDiscussion(newValue)}
                init={{
                  ...defaultTinyMCEProps.init,
                  images_upload_handler: createImagesUploadHandler(meetingId),
                }}
              />
            </FormControl>
          </GridItem>
        </>
      )}
      <GridItem>
        <MotionVotingControls
          currentMotion={currentMotion}
          requiredFields={type === RelatedType.NotRelated ? ['statement', 'reason', 'by', 'second'] : ['statement']}
          onDisplayMotion={type === RelatedType.NotRelated ? undefined : handleDisplayMotion}
          onClearMotion={handleClearMotion}
          disableVoting={type !== RelatedType.NotRelated}
          defaultVotingRule={VotingRuleType.TwoThirdsMajority}
          showVotingRuleSelector={false}
        />
      </GridItem>
    </GridContainer>
  );
};

export default RelatedMotion;
